import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AppService } from '../../services/app.service';

import { IPersonSearchResultItem } from '../../models/Person';
import { PlanSummaryService } from '../../services/plan-summary.service';
import {
    IPlanSummaryAlert,
    IPlanSummaryManipulation, IPlanSummaryResident, IPlanSummaryRotations,
    IPlanSummarySeminar, IPlanSummaryShifts, IPlanSummarySupervisor, IPlanSummaryUpcomingCourse
} from '../../models/PlanSummary';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface ICurrentRequest {
    params: ICurrentRequestParams;
    store: ICurrentRequestStore;
}

interface ICurrentRequestParams {
    studentId?: string;
    studentEmail?: string;
    programId?: string;
}

interface ICurrentRequestStore {
    resident?: IPlanSummaryResident;
    shifts?: IPlanSummaryShifts;
    seminars?: IPlanSummarySeminar[];
    manipulations?: IPlanSummaryManipulation[];
    rotations?: IPlanSummaryRotations;
    supervisor?: IPlanSummarySupervisor;
    courses?: IPlanSummaryUpcomingCourse[];
}

class CurrentRequest {
    private readonly subj = new BehaviorSubject<ICurrentRequest>({ params: {}, store: {} });

    private readonly stores: {
        [key: string]: {
            resident?: IPlanSummaryResident,
            shifts?: IPlanSummaryShifts,
            seminars?: IPlanSummarySeminar[],
            manipulations?: IPlanSummaryManipulation[],
            rotations?: IPlanSummaryRotations,
            supervisor?: IPlanSummarySupervisor
        }
    } = {};

    readonly params: ICurrentRequestParams = {};

    get store() {
        const key = this.getUniqueKey();

        if (!this.stores[key]) {
            this.stores[key] = {};
        }

        return this.stores[key];
    }

    get() {
        return this.subj.asObservable();
    }

    dispatch() {
        this.subj.next({
            params: this.params,
            store: this.store
        });
    }

    private getUniqueKey() {
        const p = this.params;
        return `${p.programId || ''}_${p.studentEmail || ''}_${p.studentId || ''}`;
    }
}

let currentRequest = new CurrentRequest();

export function getCurrentRequest(): Observable<ICurrentRequest> {
    return currentRequest.get();
}

interface ITab {
    id: 'base' | 'courses' | 'plan' | 'manipulations' | 'shifts'
        | 'seminars' | 'documents' | 'evaluation' | 'events' | 'totals';
    title: string;
    icon?: string;
}

interface IAlert {
    level: string;
    items: IPlanSummaryAlert[];
    expanded?: boolean;
}

@Component({
    selector: 'app-plan-summary',
    templateUrl: './plan-summary.component.html',
    styleUrls: ['./plan-summary.component.scss']
})
export class PlanSummaryComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: PlanSummaryService,
        private route: ActivatedRoute
    ) {
        currentRequest = new CurrentRequest();
    }

    readonly tabs: ITab[] = [
        { id: 'base', title: 'planSummary_baseInfo', icon: 'book-alt' },
        { id: 'courses', title: 'planSummary_courses', icon: 'graduation-cap' },
        { id: 'plan', title: 'planSummary_plan', icon: 'forward' },
        { id: 'manipulations', title: 'planSummary_manipulations', icon: 'edit' },
        { id: 'shifts', title: 'planSummary_shifts', icon: 'briefcase' },
        { id: 'seminars', title: 'planSummary_seminars', icon: 'calendar-lines-pen' }
    ];

    activeTabId: number = 0;
    residentInfo: IPlanSummaryResident[] = [];
    resident: IPlanSummaryResident;
    notAvailable: boolean;
    studentPickerOpened: boolean;
    canPickStudent: boolean;
    specialityId: string;
    studentId: string;
    studentEmail: string;
    programId: string;
    supervisor: IPlanSummarySupervisor;
    alerts: IAlert[] = [];

    readonly specialityDisplayFn = (option: IPlanSummaryResident) => `${option.SpecialityCode} - ${option.SpecialityName}`;
    readonly getFileUrlFn = (fileId: string) => {
        return this.service.getFileUrl(fileId, {
            studentEmail: this.studentEmail,
            studentId: this.studentId
        });
    };

    get currentTab() {
        return this.tabs[this.activeTabId];
    }

    private readonly residentCache: { [key: string]: IPlanSummaryResident[] } = {};
    private readonly alertCache: { [key: string]: IAlert[] } = {};
    private readonly supervisorCache: { [key: string]: IPlanSummarySupervisor } = {};

    ngOnInit() {
        this.studentId = this.route.snapshot.queryParams['StudentId'];
        this.studentEmail = this.route.snapshot.queryParams['StudentEmail'];
        this.programId = this.route.snapshot.queryParams['ProgramId'];

        if (this.studentId) {
            currentRequest.params.studentId = this.studentId;
        } else {
            currentRequest.params.studentEmail = this.studentEmail;
            currentRequest.params.programId = this.programId;
        }

        const user = this.app.currentUser;

        if (user) {
            this.canPickStudent = user.rights.indexOf('PLAN_SUMMARY.SET_STUDENT') > -1;
            
            if (user.hasRight('PLAN_SUMMARY.DOCUMENTS')) {
                this.tabs.push({ id: 'documents', title: 'planSummary_documents', icon: 'document' });
            }

            if (user.rights.indexOf('STUDY_QUALITY_EVALUATION.VIEW') > -1) {
                this.tabs.push({ id: 'evaluation', title: 'planSummary_qualityEvaluation', icon: 'calculator' });
            }

            if (user.rights.indexOf('PLAN_SUMMARY.EVENTS') > -1) {
                this.tabs.push({ id: 'events', title: 'planSummary_events', icon: 'clock-five' });
            }

            this.tabs.push({ id: 'totals', title: 'planSummary_totals', icon: 'checkbox' });
        }

        const queryTab = this.route.snapshot.queryParams['tab'];

        if (queryTab) {
            const tab = this.tabs.find(t => t.id == queryTab);

            if (tab) {
                this.activeTabId = this.tabs.indexOf(tab);
            }
        }

        if (this.studentId || this.studentEmail) {
            this.init();
        } else if (this.canPickStudent) {
            this.toggleStudentPicker();
        } else {
            this.init();
        }
    }

    onSpecialityChange() {
        this.resident = this.residentInfo.find(t => t.SpecialityId == this.specialityId);
        this.studentEmail = currentRequest.params.studentEmail = this.resident.Email;
        this.programId = currentRequest.params.programId = this.resident.ProgramId;

        this.init();
    }

    toggleStudentPicker() {
        this.studentPickerOpened = !this.studentPickerOpened;

        if (this.studentPickerOpened) {
            this.studentId = undefined;
            this.studentEmail = undefined;
            this.resident = undefined;
            this.residentInfo = [];
            this.notAvailable = false;
        }
    }

    pickStudent(student: IPersonSearchResultItem) {
        this.studentPickerOpened = false;
        this.programId = null;
        this.studentId = currentRequest.params.studentId = null;
        this.studentEmail = currentRequest.params.studentEmail = student.Email;
        this.init();
    }

    private init() {
        this.getResident().subscribe(data => {
            this.residentInfo = data;

            if (data.length) {
                this.resident = this.programId ? data.find(t => t.ProgramId == this.programId) : data[0];

                if (this.resident) {
                    this.programId = this.resident.ProgramId;
                    this.specialityId = this.resident.SpecialityId;

                    currentRequest.params.programId = this.programId;

                    currentRequest.store.resident = this.resident;

                    currentRequest.dispatch();

                    this.loadSupervisor(currentRequest.params);
                    this.loadAlerts(this.studentEmail);
                } else {
                    this.notAvailable = true;
                }
            } else {
                this.notAvailable = true;
                this.alerts = [];
            }
        }, err => {
            this.notAvailable = true;
            this.alerts = [];
            throw err;
        });
    }

    private getResident() {
        const key = `${this.studentId}_${this.studentEmail}`;

        if (this.residentCache[key]) {
            return of(this.residentCache[key]);
        }

        return this.app.addLoading(this.service.getResident({
            studentId: this.studentId,
            studentEmail: this.studentEmail
        })).pipe(tap(data => {
            this.residentCache[key] = data;
        }));
    }

    private loadSupervisor(params: ICurrentRequestParams) {
        const key = JSON.stringify(params);

        if (this.supervisorCache[key]) {
            this.supervisor = this.supervisorCache[key];
        } else {
            this.app.addLoading(this.service.getSupervisor(params)).subscribe(data => {
                this.supervisor = data || null;
                this.supervisorCache[key] = data;

                currentRequest.store.supervisor = data;
            });
        }
    }

    private loadAlerts(email: string) {
        if (this.alertCache[email]) {
            this.alerts = this.alertCache[email];
        } else {
            this.app.addLoading(this.service.getAlerts(email)).subscribe(data => {
                const alerts: IAlert[] = [];
                const groups: { [key: string]: IPlanSummaryAlert[] } = {};

                data.forEach(t => {
                    const key = t.Level;

                    if (!groups[key]) groups[key] = [];

                    groups[key].push(t);
                });

                for (const g in groups) {
                    alerts.push({
                        level: g,
                        items: groups[g]
                    });
                }

                this.alertCache[email] = alerts;
                this.alerts = alerts;
            });
        }
    }
}
